// NEC 220.87 / 120.87 Condition (1) — measured demand engine. The methodology
// is LOCKED in context/nec-methodology.md. Under every edition before 2026,
// and under rules revision 1, new load enters at 100% of its rating. Under
// the 2026 edition from rules revision 2, new load that passes the season
// filter takes the Table 120.83 percentages (newLoadFactorsApply).

import { Project, Equipment, AMIDataPoint } from '../../../types/project';
import { serviceCapacityKVA as computeServiceCapacityKVA } from '../../serviceConfig';
import { resolvedEditionKey, getEditionPack, New83Percentages } from '../editions';
import { calcRulesFor } from '../rulesVersion';
import { countsAsCentralResistanceHeat } from '../loadRoles';

export interface CalculationResults {
  maxRecordedDemandKW: number;
  maxRecordedDemandKVA: number;
  peakTimestamp: string | null;
  peakSeason: string;
  demandAdjustedForSolar: boolean;

  serviceCapacityKVA: number;

  existingDemandKVA: number;
  existingDemand125KVA: number;

  /** Connected (nameplate or MCA as typed) kVA of the concurrent new loads. */
  totalConcurrentNewLoadKVA: number;
  /** What the concurrent new loads add to the adjusted demand. Equal to
   * totalConcurrentNewLoadKVA unless Table 120.83 factors apply. */
  totalConcurrentNewLoadDemandKVA: number;
  totalAllNewLoadKVA: number;

  /** True when the 2026 Table 120.83 demand factors were applied to new load
   * (rules revision 2+, 2026 edition, preparer election on). */
  newLoadFactorsApplied: boolean;
  /** True when the adjusted existing demand left part of Table 120.83's first
   * tier unused and new load filled it at 100% (position P4). */
  newLoadTierFillApplied: boolean;
  /** The same evaluation with every concurrent new load at 100% — the reading
   * an AHJ may require. Equal to the headline figures when no factor applies. */
  adjustedDemandAt100KVA: number;
  adequacyAt100: 'PASS' | 'FAIL';

  /** Equipment taken out with this work. Always listed; credited only when
   * removedLoadCreditApplies (2026, rules revision 3+). */
  removedCalculations: RemovedCalculation[];
  /** True when removed load is subtracted from the adjusted demand. */
  removedLoadCreditApplied: boolean;
  /** kVA subtracted from the adjusted demand (0 when not credited). */
  totalRemovedCreditKVA: number;

  adjustedDemandKVA: number;
  availableMarginKVA: number;
  utilizationPercent: number;

  adequacyResult: 'PASS' | 'FAIL';

  equipmentCalculations: EquipmentCalculation[];
}

export interface EquipmentCalculation {
  equipmentId: string;
  equipmentName: string;
  /** Connected kVA, as typed. */
  kva: number;
  isConcurrent: boolean;
  /** Table 120.83 percentage as a fraction (1 when no factor applies). */
  demandFactor: number;
  /** Table 120.83 row the load falls in, or null when no factor applies. */
  factorCategory: NewLoadCategory | null;
  /** What this row adds to the adjusted demand: 0 when not concurrent. */
  demandKVA: number;
}

export type NewLoadCategory = 'evse' | 'centralResistanceHeat' | 'other';

export interface RemovedCalculation {
  equipmentId: string;
  equipmentName: string;
  /** Connected kVA, as typed. */
  kva: number;
  /** Runs in the season of the measured peak — the same season filter as new load. */
  isConcurrent: boolean;
  /** Fraction of the rating credited (REMOVED_LOAD_RATE), or 0 when not credited. */
  creditFactor: number;
  /** kVA subtracted from the adjusted demand. */
  creditKVA: number;
}

/**
 * Table 120.83's rate for existing load past the first tier. Removed equipment
 * is existing load, so under the same reading that gives new load its 50/80%,
 * its removal is worth 40% of its rating (Jake, 2026-10-13, option A).
 */
export const REMOVED_LOAD_RATE = 0.4;

/**
 * Whether removed load is subtracted. 120.87(2) (2026) adds "the new load
 * minus any removed load determined in accordance with Article 120 Parts I,
 * III, and IV". The subtraction itself is code text, so it does not follow the
 * preparer's new-load election; it needs the 2026 edition and rules revision 3
 * (a report completed earlier keeps its numbers). No earlier edition says
 * anything about removed load, so there it is listed and never credited.
 */
export function removedLoadCreditApplies(project: Partial<Project>): boolean {
  if (calcRulesFor(project) < 3) return false;
  return resolvedEditionKey(project) === '2026';
}

/**
 * Whether this project's new load takes the 2026 Table 120.83 percentages.
 *
 * 120.87 Condition (2) (2026) adds "the new load minus any removed load
 * determined in accordance with Article 120 Parts I, III, and IV". TrueLoad
 * reads the phrase as governing the new load (Jake, 2026-10-12) and applies
 * Part IV's existing-dwelling table. Three gates: the project calculates
 * under rules revision 2 or later (completed reports stay as filed), it
 * resolves to the 2026 edition (no other edition's text says this), and the
 * preparer has not switched the election off for an AHJ that wants 100%.
 */
export function newLoadFactorsApply(project: Partial<Project>): boolean {
  if (calcRulesFor(project) < 2) return false;
  if (!getEditionPack(resolvedEditionKey(project)).new83Percentages) return false;
  return project.measured_new_load_factors !== false;
}

/** Same classification as the traditional 2026 engine (existing22083.ts
 * classify), so one load cannot fall in two table rows on two paths. A heat
 * pump is "all other new loads"; its backup strips belong on their own row. */
function categoryOf(eq: Equipment, calcRules: number): NewLoadCategory {
  if (eq.equipment_type === 'EVSE') return 'evse';
  if (countsAsCentralResistanceHeat(eq, calcRules)) return 'centralResistanceHeat';
  return 'other';
}

function rateOf(category: NewLoadCategory, rates: New83Percentages): number {
  if (category === 'evse') return rates.newEvse;
  if (category === 'centralResistanceHeat') return rates.newCentralResistanceHeat;
  return rates.otherNewLoads;
}

export function calculateNEC22087(
  project: Project,
  equipment: Equipment[],
  amiData?: AMIDataPoint[],
  removed: Equipment[] = []
): CalculationResults {
  let maxRecordedDemandKW = project.max_recorded_demand_kw || 0;
  let peakTimestamp = project.peak_timestamp;
  let demandAdjustedForSolar = false;

  if (project.ami_generation_column_detected === true && project.gross_peak_kw != null && project.gross_peak_kw > 0) {
    maxRecordedDemandKW = project.gross_peak_kw;
    demandAdjustedForSolar = true;
  } else if (amiData && amiData.length > 0) {
    const peak = findPeakDemand(amiData);
    maxRecordedDemandKW = peak.kw;
    peakTimestamp = peak.timestamp;
  }

  const maxRecordedDemandKVA = maxRecordedDemandKW;

  const peakSeason = determineSeason(peakTimestamp);

  const serviceCapacityKVA = computeServiceCapacityKVA(
    project.service_rating_amps,
    project.service_voltage_numeric,
    project.service_phase === 'three' ? 'three' : 'single'
  );

  const existingDemandKVA = maxRecordedDemandKVA;
  const existingDemand125KVA = existingDemandKVA * 1.25;

  const pack = getEditionPack(resolvedEditionKey(project));
  const rates = newLoadFactorsApply(project) ? pack.new83Percentages ?? null : null;
  const rules = calcRulesFor(project);

  const base = equipment.map((eq) => calculateEquipmentKVA(eq));

  // The season filter runs first and is unchanged: a load that will not run
  // in the season of the measured peak is noncoincident (120.6, Part I — the
  // first Part the 2026 sentence names) and adds nothing. Only the loads that
  // count take the table's percentage.
  //
  // Table 120.83 applies its first tier at 100% to existing and new load
  // combined. The existing load here is the 125% figure. Where that leaves
  // part of the tier unused, new load fills it lowest-percentage-first, the
  // order that gives the highest demand — position P4, same as the
  // traditional engine.
  let tierLeftKVA = rates ? Math.max(0, pack.optional83TierKVA - existingDemand125KVA) : 0;
  let tierFill = false;
  const absorbed = new Map<string, number>();
  if (rates && tierLeftKVA > 0) {
    const order = equipment
      .map((eq, i) => ({ eq, calc: base[i] }))
      .filter(({ calc }) => calc.isConcurrent && calc.kva > 0)
      .sort((a, b) => rateOf(categoryOf(a.eq, rules), rates) - rateOf(categoryOf(b.eq, rules), rates));
    for (const { calc } of order) {
      if (tierLeftKVA <= 0) break;
      const take = Math.min(calc.kva, tierLeftKVA);
      absorbed.set(calc.equipmentId, take);
      tierLeftKVA -= take;
      tierFill = true;
    }
  }

  const equipmentCalculations: EquipmentCalculation[] = equipment.map((eq, i) => {
    const calc = base[i];
    if (!rates) {
      return { ...calc, demandFactor: 1, factorCategory: null, demandKVA: calc.isConcurrent ? calc.kva : 0 };
    }
    const category = categoryOf(eq, rules);
    const rate = rateOf(category, rates);
    const atFull = absorbed.get(calc.equipmentId) ?? 0;
    const demandKVA = calc.isConcurrent ? atFull + rate * (calc.kva - atFull) : 0;
    return { ...calc, demandFactor: rate, factorCategory: category, demandKVA };
  });

  const totalConcurrentNewLoadKVA = equipmentCalculations
    .filter((calc) => calc.isConcurrent)
    .reduce((sum, calc) => sum + calc.kva, 0);

  const totalConcurrentNewLoadDemandKVA = equipmentCalculations.reduce(
    (sum, calc) => sum + calc.demandKVA,
    0
  );

  const totalAllNewLoadKVA = equipmentCalculations.reduce(
    (sum, calc) => sum + calc.kva,
    0
  );

  // Removed load. The order is the section's: 125% of the measured demand,
  // plus the new load, minus the removed load — so the credit is never
  // multiplied by 1.25. A removed load that does not run in the peak's season
  // contributed nothing to the measured peak, so its removal frees nothing.
  // The credit can never exceed the measured demand it comes out of.
  const credited = removedLoadCreditApplies(project);
  const removedCalculations: RemovedCalculation[] = removed.map((eq) => {
    const calc = calculateEquipmentKVA(eq);
    const creditFactor = credited && calc.isConcurrent ? REMOVED_LOAD_RATE : 0;
    return { ...calc, creditFactor, creditKVA: creditFactor * calc.kva };
  });
  const totalRemovedCreditKVA = Math.min(
    removedCalculations.reduce((sum, r) => sum + r.creditKVA, 0),
    existingDemandKVA
  );

  // The full-rating alternative changes the new-load reading only; the
  // removed-load subtraction is code text either way.
  const adjustedDemandKVA =
    existingDemand125KVA + totalConcurrentNewLoadDemandKVA - totalRemovedCreditKVA;
  const adjustedDemandAt100KVA =
    existingDemand125KVA + totalConcurrentNewLoadKVA - totalRemovedCreditKVA;

  const availableMarginKVA = serviceCapacityKVA - adjustedDemandKVA;

  const utilizationPercent = (adjustedDemandKVA / serviceCapacityKVA) * 100;

  const adequacyResult: 'PASS' | 'FAIL' =
    adjustedDemandKVA <= serviceCapacityKVA ? 'PASS' : 'FAIL';
  const adequacyAt100: 'PASS' | 'FAIL' =
    adjustedDemandAt100KVA <= serviceCapacityKVA ? 'PASS' : 'FAIL';

  return {
    maxRecordedDemandKW,
    maxRecordedDemandKVA,
    peakTimestamp,
    peakSeason,
    demandAdjustedForSolar,
    serviceCapacityKVA,
    existingDemandKVA,
    existingDemand125KVA,
    totalConcurrentNewLoadKVA,
    totalConcurrentNewLoadDemandKVA,
    totalAllNewLoadKVA,
    newLoadFactorsApplied: rates !== null,
    newLoadTierFillApplied: tierFill,
    adjustedDemandAt100KVA,
    adequacyAt100,
    removedCalculations,
    removedLoadCreditApplied: credited,
    totalRemovedCreditKVA,
    adjustedDemandKVA,
    availableMarginKVA,
    utilizationPercent,
    adequacyResult,
    equipmentCalculations,
  };
}

function findPeakDemand(amiData: AMIDataPoint[]): {
  kw: number;
  timestamp: string;
} {
  if (amiData.length === 0) {
    return { kw: 0, timestamp: '' };
  }

  let maxKW = amiData[0].kw;
  let maxTimestamp = amiData[0].timestamp;

  for (const point of amiData) {
    if (point.kw > maxKW) {
      maxKW = point.kw;
      maxTimestamp = point.timestamp;
    }
  }

  return { kw: maxKW, timestamp: maxTimestamp };
}

export function determineSeason(timestamp: string | null): string {
  if (!timestamp) return 'Unknown';

  const date = new Date(timestamp);
  // Fixed-UTC wall clock: the meter's month is the UTC month.
  const month = date.getUTCMonth() + 1;

  if (month >= 12 || month <= 2) return 'Winter';
  if (month >= 3 && month <= 5) return 'Spring';
  if (month >= 6 && month <= 8) return 'Summer';
  return 'Fall';
}

// Capacity lives in serviceConfig.ts alongside the configuration list, so the
// formula and the phase that selects it cannot drift apart.

function calculateEquipmentKVA(
  equipment: Equipment
): Omit<EquipmentCalculation, 'demandFactor' | 'factorCategory' | 'demandKVA'> {
  let kva = 0;

  if (equipment.kw !== null) {
    kva = equipment.kw * equipment.quantity;
  } else if (equipment.amps !== null) {
    kva = ((equipment.amps * equipment.voltage) / 1000) * equipment.quantity;
  }

  return {
    equipmentId: equipment.id,
    equipmentName: equipment.equipment_name,
    kva,
    isConcurrent: equipment.is_concurrent,
  };
}
